import type { LoftHatchElement, Point } from '../types';

export const drawLoftHatch = (ctx: CanvasRenderingContext2D, element: LoftHatchElement, scale: number) => {
  const { position, width, height = 60, isInsulated, isDraughtProofed } = element;
  
  ctx.save();
  
  // Loft hatch main rectangle
  ctx.fillStyle = '#e6f3ff'; // Light blue background
  ctx.strokeStyle = '#0066cc'; // Dark blue border
  ctx.lineWidth = 2 / scale;
  
  const drawWidth = width;
  const drawHeight = height;
  const x = position.x - drawWidth / 2;
  const y = position.y - drawHeight / 2;
  
  // Main hatch rectangle
  ctx.fillRect(x, y, drawWidth, drawHeight);
  ctx.strokeRect(x, y, drawWidth, drawHeight);
  
  // Add insulation indicator if insulated
  if (isInsulated) {
    ctx.fillStyle = '#ffff99'; // Yellow for insulation
    ctx.fillRect(x + 2, y + 2, drawWidth - 4, drawHeight - 4);
  }
  
  // Add draught proofing indicator if draught proofed
  if (isDraughtProofed) {
    ctx.strokeStyle = '#ff6600'; // Orange for draught proofing
    ctx.lineWidth = 3 / scale;
    ctx.strokeRect(x + 1, y + 1, drawWidth - 2, drawHeight - 2);
  }
  
  // Add text label
  ctx.fillStyle = '#000000';
  ctx.font = `${Math.max(8, 12 / scale)}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('LH', position.x, position.y);
  
  ctx.restore();
};

export const drawLoftHatchPreview = (ctx: CanvasRenderingContext2D, element: LoftHatchElement | null, scale: number) => {
  if (!element) return;
  
  ctx.save();
  
  // Preview with dashed lines
  ctx.setLineDash([5, 5]);
  ctx.strokeStyle = '#0066cc';
  ctx.lineWidth = 2 / scale;
  ctx.globalAlpha = 0.7;
  
  const { position, width, height = 60 } = element;
  const drawWidth = width;
  const drawHeight = height;
  const x = position.x - drawWidth / 2;
  const y = position.y - drawHeight / 2;
  
  // Preview rectangle
  ctx.strokeRect(x, y, drawWidth, drawHeight);
  
  // Preview text
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#0066cc';
  ctx.font = `${Math.max(8, 12 / scale)}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('LH', position.x, position.y);
  
  ctx.restore();
};

export const drawLoftHatches = (ctx: CanvasRenderingContext2D, elements: LoftHatchElement[], currentFloor: number, scale: number) => {
  // Filter loft hatches by current floor
  const loftHatchesOnCurrentFloor = elements.filter(element => element.floor === currentFloor);
  
  loftHatchesOnCurrentFloor.forEach(element => {
    drawLoftHatch(ctx, element, scale);
  });
};

export const createLoftHatch = (position: Point, floor: number): LoftHatchElement => {
  return {
    id: `loft-hatch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'loft-hatch',
    position,
    floor,
    width: 60, // Default width in pixels (roughly 600mm)
    height: 60, // Default height in pixels (roughly 600mm)
    isInsulated: false,
    isDraughtProofed: false,
    lengthMM: 600,
    widthMM: 600
  };
}; 